import { Collection } from 'firestorter';
import Collections from '../../utils/collections';
import BaseDocument from '../../Model/BaseDocument';
import feedType, { coachActivityFeedTypes } from '../context/FeedContext/config/feedTypes';

/**
 * Types that define activities based on the selected type when adding activities for the workout
 */
const ActivityType = {
  CIRCUIT: 'CIRCUIT',
};

const activityStatus = {
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
  REMOVED: 'REMOVED',
  /*
    A highlight task held back until the client agrees in the app ("Ask me each time before use"). Nothing
    the coach sees may show it: the client's app moves it to IN_PROGRESS on a yes and to REMOVED on a no, and
    the AC can only remove it, which withdraws its highlight. Nothing expires it.
  */
  AWAITING_CLIENT_CONSENT: 'AWAITING_CLIENT_CONSENT',
};

/** Why a task was removed, when it was not a person on the team removing it. */
const TaskRemovedReason = {
  CLIENT_DECLINED_SHARE: 'CLIENT_DECLINED_SHARE',
};

const activeActivityStatus = [
  activityStatus.IN_PROGRESS,
  activityStatus.COMPLETED,
];

// Firestore caps an `in` filter at 10 values; coachActivityFeedTypes can exceed that, so the unarchived
// feed is queried in chunks and the docs are merged. Each chunk keeps a single inequality
// (status !=) alongside the `in` (type), which Firestore allows.
const FIRESTORE_IN_LIMIT = 10;

class CoachActivity extends BaseDocument {
  constructor(id, opts) {
    super(`${Collections.COACH_ACTIVITY}/${id}`, opts);
  }

  get status() {
    return this.data.status;
  }

  get createdAt() {
    return this.data.createdAt?.toDate();
  }

  get time() {
    return this.data.time?.toDate();
  }

  get type() {
    return this.data.type;
  }

  get coach() {
    return this.data.coach;
  }

  get user() {
    return this.data.user;
  }

  get productId() {
    return this.data.productId;
  }

  get content() {
    return this.data.content;
  }

  get channelId() {
    return this.data.channelId;
  }

  // Coach tasks created from a chat message link to it (the message the coach
  // should respond to). messagePreview is denormalized so the task surfaces render
  // without fetching the message.
  get messageId() {
    return this.data.messageId;
  }

  get messagePreview() {
    return this.data.messagePreview;
  }

  get highlightId() {
    return this.data.highlightId;
  }

  get highlight() {
    return this.data.highlight;
  }

  get workoutAssignmentId() {
    return this.data.workoutAssignmentId;
  }

  get workoutVideoAvailable() {
    return this.data.workoutVideoAvailable;
  }

  get gameplaySessionId() {
    return this.data.gameplaySessionId;
  }

  get alertType() {
    return this.data.alertType;
  }

  get checkInId() {
    return this.data.checkInId;
  }

  get checkInData() {
    return this.data.checkInData || {};
  }

  get habitData() {
    return this.data.habitData || {};
  }

  get workoutData() {
    return this.data.workoutData || [];
  }

  get mealsSummary() {
    return this.data.mealsSummary || {};
  }

  get userName() {
    return this.data.userName;
  }

  get userAvatar() {
    return this.data.userAvatar;
  }

  get lastInteractionBy() {
    return this.data.lastInteractionBy;
  }

  get lastInteractionByUserName() {
    return this.data.lastInteractionByUserName;
  }

  get enabledFeatures() {
    return this.data.enabledFeatures || [];
  }

  get isFirstCheckIn() {
    return this.data.isFirstCheckIn || false;
  }

  get isFutureStart() {
    return !!this.data.isFutureStart;
  }

  get weeklyWeightGoal() {
    return this.data.weeklyWeightGoal || '';
  }

  /**
   * The time when the meal plan automation was scheduled to run.
   * @returns {Date}
   */
  get mealPlanAutomationTime() {
    return this.data.mealPlanAutomationTime?.toDate();
  }

  /**
   * The time when the track automation was scheduled to run.
   * @returns {Date}
   */
  get trackAutomationTime() {
    return this.data.trackAutomationTime?.toDate();
  }

  get oldType() {
    return this.data.oldType;
  }

  // Coach task fields (populated only when type === COACH_TASK). See S2-7988.
  get taskType() {
    return this.data.taskType;
  }

  get taskTitle() {
    return this.data.taskTitle;
  }

  get priority() {
    return this.data.priority;
  }

  get deadline() {
    return this.data.deadline?.toDate();
  }

  get instructions() {
    return this.data.instructions;
  }

  get attachments() {
    return this.data.attachments || [];
  }

  get assignedBy() {
    return this.data.assignedBy;
  }

  get assignedByName() {
    return this.data.assignedByName;
  }

  get completedAt() {
    return this.data.completedAt?.toDate();
  }

  // Lifecycle accountability — assigner (assignedBy), completer, editor and remover
  // can all be different people.
  get completedBy() {
    return this.data.completedBy;
  }

  get updatedBy() {
    return this.data.updatedBy;
  }

  get updatedAt() {
    return this.data.updatedAt?.toDate();
  }

  get removedBy() {
    return this.data.removedBy;
  }

  get removedAt() {
    return this.data.removedAt?.toDate();
  }

  // Overdue is derived, never stored: an in-progress task past its deadline.
  get isOverdue() {
    return this.status === activityStatus.IN_PROGRESS && !!this.deadline && this.deadline < new Date();
  }

  get removedReason() {
    return this.data.removedReason;
  }

  get isAwaitingClientConsent() {
    return this.status === activityStatus.AWAITING_CLIENT_CONSENT;
  }

  get isDeclinedByClient() {
    return this.status === activityStatus.REMOVED && this.removedReason === TaskRemovedReason.CLIENT_DECLINED_SHARE;
  }

  get trackAssignmentId() {
    return this.data.trackAssignmentId;
  }

  get newPreferences() {
    return this.data.newPreferences || {};
  }

  get isTrackRemoved() {
    return !!this.data.isTrackRemoved;
  }

  get progressionData() {
    return this.data.progressionData;
  }

  static async getUnarchivedActivitiesForCoach(coach) {
    // coachActivityFeedTypes can exceed Firestore's 10-value `in` limit, so query it in chunks (one
    // Collection per chunk) and expose the merged docs. Reading `docs` inside a mobx reaction
    // subscribes to every chunk, so the feed stays reactive — each 'auto' collection goes
    // real-time while observed — exactly as the single-collection query did.
    const collections = [];
    for (let i = 0; i < coachActivityFeedTypes.length; i += FIRESTORE_IN_LIMIT) {
      const typeChunk = coachActivityFeedTypes.slice(i, i + FIRESTORE_IN_LIMIT);
      collections.push(new Collection(Collections.COACH_ACTIVITY, {
        createDocument: ({ id }, opts) => new CoachActivity(id, { ...opts, disableObserverRefCount: true }),
        query: (ref) => ref
          .where('coach', '==', coach)
          .where('status', '!=', activityStatus.REMOVED)
          .where('type', 'in', typeChunk)
          .orderBy('status', 'desc')
          .orderBy('createdAt', 'desc'),
      }));
    }
    await Promise.all(collections.map((collection) => collection.fetch()));
    return {
      // Merge the chunks, restoring the single-query order (status desc, then createdAt desc)
      // the feed relies on — chunking only orders within each chunk.
      get docs() {
        return collections
          .flatMap((collection) => collection.docs)
          .sort((a, b) => {
            if (a.status !== b.status) {
              return a.status < b.status ? 1 : -1;
            }
            return (b.createdAt?.getTime() ?? 0) - (a.createdAt?.getTime() ?? 0);
          });
      },
    };
  }

  static async getArchivedActivitiesForCoach(coach) {
    const activeActivitiesCollection = new Collection(Collections.COACH_ACTIVITY, {
      createDocument: ({ id }, opts) => new CoachActivity(id, { ...opts, disableObserverRefCount: true }),
      query: (ref) => ref
        .where('coach', '==', coach)
        .where('type', '==', feedType.ARCHIVED)
        .where('status', 'in', activeActivityStatus)
        .orderBy('type', 'desc')
        .orderBy('createdAt', 'desc')
        .limit(50),
    });
    await activeActivitiesCollection.fetch();
    return activeActivitiesCollection;
  }
}

export default CoachActivity;
export {
  ActivityType,
  activityStatus,
  TaskRemovedReason,
};
